import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Tag,
  Tabs,
  Input,
  Spin,
  Progress,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Task, TaskSubTask, TaskDraftReport, TaskReview } from '../services/tasks';
import customerApi from '../services/customerApi';
import { customerAuthService } from '../services/customerAuth';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const CustomerTaskDetail: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const customer = customerAuthService.getCurrentCustomer();
  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (id) {
      loadTask(parseInt(id, 10));
    }
  }, [id]);

  const loadTask = async (taskId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/tasks/${taskId}`);
      const loadedTask = response.data.data || response.data;
      setTask(loadedTask);
    } catch (error: any) {
      console.error('Failed to load task:', error);
      let errorMessage = 'Failed to load task';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Task not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/personal');
    } finally {
      setLoading(false);
    }
  };

  const formatTaskNumber = (taskId: number, referenceNumber?: string) => {
    return referenceNumber || `#${taskId.toString().padStart(7, '0')}`;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleApprove = async () => {
    if (!task) return;
    try {
      await customerApi.post('/customer/task-reviews', {
        task_id: task.id,
        customer_id: customer?.id,
        review: 'Approved by customer',
      });
      
      message.success('Task approved successfully');
      loadTask(task.id);
    } catch (error: any) {
      console.error('Failed to approve task:', error);
      let errorMessage = 'Failed to approve task';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    }
  };

  const handleReviewSubmit = async () => {
    if (!task || !reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await customerApi.post('/customer/task-reviews', {
        task_id: task.id,
        customer_id: customer?.id,
        review: reviewText,
      });
      
      message.success('Review submitted successfully');
      setReviewText('');
      loadTask(task.id);
    } catch (error: any) {
      console.error('Failed to submit review:', error);
      let errorMessage = 'Failed to submit review';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    } finally {
      setSubmittingReview(false);
    }
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]): string => {
    if (!task) return '-';
    
    const value = (task as any)[fieldName];
    if (value === null || value === undefined) return '-';
    
    if (type === 'date') {
      return value ? dayjs(value).format('DD/MM/YYYY') : '-';
    }
    
    if (type === 'number') {
      return value ? value.toString() : '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option ? option.name : '-';
    }
    
    if (type === 'text') {
      return value ? value.toString() : '-';
    }
    
    return '-';
  };

  if (loading || !task) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const taskSubTasks = (task as any).subTasks || (task as any).sub_tasks || [];
  const draftReports = (task as any).draftReports || (task as any).draft_reports || [];
  const reviews = (task as any).reviews || [];
  const customerReviews = reviews.filter((review: TaskReview) => review.customer_id);
  const checklistPercent = (() => {
    const total = taskSubTasks.length;
    if (total === 0) return 0;
    const completed = taskSubTasks.filter((item: TaskSubTask) => item.completed).length;
    return Math.round((completed / total) * 100);
  })();

  const getTaskDisplayName = () => {
    return formatTaskNumber(task.id, task.reference_number);
  };

  const statuses: Array<'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed'> = ['Plan', 'Progress', 'Review', 'Approve', 'Completed'];
  const statusConfig = {
    Plan: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff' },
    Progress: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00' },
    Review: { bg: '#F9F0FF', border: '#9254DE', color: '#722ED1', dot: '#722ED1' },
    Approve: { bg: '#E6FFFB', border: '#36CFC9', color: '#13C2C2', dot: '#13C2C2' },
    Completed: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A' },
  };
  const currentStatus = task.status || 'Plan';

  // Determine customer type (personal or business)
  const customerType = (task as any).business_id ? 'business' : 'personal';
  const customerName = task.customer?.name || '-';
  const businessName = (task as any).business?.name || '-';

  return (
    <div>
      {/* Header */}
      <div style={{ background: '#fff', padding: '16px 24px', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            {getTaskDisplayName()}
          </Title>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/customer/personal')}
            >
              Back
            </Button>
            {task.status === 'Review' && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleApprove}
              >
                Approve
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/* Tabs */}
      <Card style={{ background: '#fff' }}>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Space size={8}>
              {statuses.map((status) => {
                const isActive = currentStatus === status;
                const config = statusConfig[status];
                return (
                  <div
                    key={status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: isActive ? config.bg : '#f5f5f5',
                      border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                      color: isActive ? config.color : '#595959',
                      fontWeight: isActive ? 500 : 400,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'all 0.2s',
                    }}
                  >
                    <span style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: isActive ? config.dot : '#8c8c8c',
                    }}></span>
                    {status}
                  </div>
                );
              })}
            </Space>
          }
        >
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 320 }}>
                <Card bodyStyle={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {/* Customer - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {customerType === 'business' ? businessName : customerName}
                          </div>
                        </td>
                      </tr>
                      {/* Type 和 Year - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Type</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('task_type_id', 'select', [task.taskType].filter(Boolean))}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Year</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('year', 'text')}
                          </div>
                        </td>
                      </tr>
                      {/* Task - 单独一行，合并后三列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Task</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('task_template_id', 'select', [task.taskTemplate].filter(Boolean))}
                          </div>
                        </td>
                      </tr>
                      {/* Create 和 Due - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Create</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('created_date', 'date')}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Due</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('due_date', 'date')}
                          </div>
                        </td>
                      </tr>
                      {/* Plan 和 Deadline - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Plan</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('plan_date', 'date')}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Deadline</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {getDisplayValue('deadline', 'date')}
                          </div>
                        </td>
                      </tr>
                      {/* Quotation ID 和 Invoice ID - 同一行，各占两列 */}
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Quotation ID</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {(task as any).quotation?.quotation_number || '-'}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Invoice ID</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {(task as any).invoice?.invoice_number || '-'}
                          </div>
                        </td>
                      </tr>
                      {/* Team 和 Staff - 同一行，各占两列 */}
                      <tr>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Team</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {task.customer?.team?.name || '-'}
                          </div>
                        </td>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Staff</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          <div>
                            {(task as any).staff?.name || (task as any).quotation?.staff?.name || '-'}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card>
              </div>
              <Card style={{ width: 220, textAlign: 'center' }}>
                <Text type="secondary">Progress</Text>
                <div style={{ marginTop: 12 }}>
                  <Progress type="circle" percent={checklistPercent} width={96} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{checklistPercent}%</Text>
                </div>
              </Card>
            </div>
          </TabPane>

          {/* Draft Tab */}
          <TabPane tab="Draft" key="draft">
            <Card title="Draft Reports" bodyStyle={{ padding: '16px' }}>
              {draftReports.map((report: TaskDraftReport) => {
                const fileName = report.attachment ? report.attachment.split('/').pop() : 'No attachment';
                const handleView = async () => {
                  try {
                    const response = await customerApi.get(`/customer/task-draft-reports/${report.id}/view`, {
                      responseType: 'blob',
                    });
                    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                    const blobUrl = window.URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                  } catch (error: any) {
                    message.error('Failed to view file');
                  }
                };
                const handleDownload = async () => {
                  try {
                    const response = await customerApi.get(`/customer/task-draft-reports/${report.id}/download`, {
                      responseType: 'blob',
                    });
                    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = fileName || 'draft-report';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                  } catch (error: any) {
                    message.error('Failed to download file');
                  }
                };
                return (
                  <div key={report.id} style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 4 }}>
                      {report.attachment ? (
                        <Space>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={handleView}
                            size="small"
                          >
                            {fileName}
                          </Button>
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            size="small"
                          />
                        </Space>
                      ) : (
                        <Text strong>No attachment</Text>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {report.creator?.name || 'Unknown'} • {formatDateTime(report.created_at)}
                    </div>
                  </div>
                );
              })}
              {draftReports.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No draft reports
                </div>
              )}
            </Card>
          </TabPane>

          {/* Review Tab */}
          <TabPane tab="Review" key="review">
            <Card title="Customer Reviews" bodyStyle={{ padding: '16px' }}>
              <div style={{ marginBottom: 24 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <TextArea
                    rows={6}
                    placeholder="Enter your review..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={handleReviewSubmit}
                    loading={submittingReview}
                    disabled={!reviewText.trim()}
                  >
                    Submit
                  </Button>
                </Space>
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
                {customerReviews.length > 0 ? (
                  <div>
                    {customerReviews.map((review: TaskReview) => (
                      <div
                        key={review.id}
                        style={{
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <Text>{review.review || '-'}</Text>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          By {review.customer?.name || review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No customer reviews submitted yet
                  </div>
                )}
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};
